// PKCE-style public code exchange for the Outlook SSO login (DEV-1032).
//
// The login dialog creates a random state and code_verifier, keeps both in
// sessionStorage (the dialog stays in one browsing context across the
// kvasir/IdP hops, so it is still there on the way back) and sends the state
// and SHA-256(code_verifier) to kvasir with POST /session/user. kvasir sends the
// dialog back to /login?code=&state=, and the dialog trades the code for the
// session token with POST /session/sso/exchange, proving with the verifier
// that it is the browser that started the login. The token never appears in a
// URL.

import { KVASIR_BASE_URL } from '../../config';

const STATE_KEY = 'precisely-sso-state';
const VERIFIER_KEY = 'precisely-sso-verifier';
const EMAIL_KEY = 'precisely-sso-email';

export interface PkceStart {
  state: string;
  challenge: string;
}

export interface StoredPkce {
  state: string;
  verifier: string;
  email: string;
}

export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** 32 random bytes, base64url without padding (43 characters). */
export function randomToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** base64url-no-padding(SHA-256(verifier)), the S256 code challenge. */
export async function codeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Creates and stores a fresh state and verifier, and returns what goes to
 * kvasir. Each call replaces the previous login attempt's values.
 */
export async function startPkce(email: string): Promise<PkceStart> {
  const state = randomToken();
  const verifier = randomToken();
  const challenge = await codeChallenge(verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(EMAIL_KEY, email);
  return { state, challenge };
}

export function readStoredPkce(): StoredPkce | null {
  try {
    const state = sessionStorage.getItem(STATE_KEY);
    const verifier = sessionStorage.getItem(VERIFIER_KEY);
    if (!state || !verifier) return null;
    return { state, verifier, email: sessionStorage.getItem(EMAIL_KEY) || '' };
  } catch {
    return null;
  }
}

export function clearStoredPkce(): void {
  try {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
  } catch {
    // sessionStorage unavailable: nothing stored to clear.
  }
}

/** Constant-time comparison, so the state check does not leak a prefix. */
function sameString(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

export type ExchangeResult =
  | { ok: true; token: string; userUuid: string; email: string }
  | {
      ok: false;
      reason: 'state_mismatch' | 'rejected' | 'rate_limited' | 'error';
      email: string;
    };

/**
 * Checks the returned state against the stored one and exchanges the code for
 * the session token. The stored values are cleared whatever the outcome: the
 * code is single-use, so a retry has to start a new login.
 */
export async function exchangeCode(
  code: string,
  state: string,
): Promise<ExchangeResult> {
  const stored = readStoredPkce();
  clearStoredPkce();
  const email = stored?.email || '';

  if (!stored || !state || !sameString(stored.state, state)) {
    return { ok: false, reason: 'state_mismatch', email };
  }

  let response: Response;
  try {
    response = await fetch(KVASIR_BASE_URL() + '/session/sso/exchange', {
      method: 'POST',
      credentials: 'omit',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, state, code_verifier: stored.verifier }),
    });
  } catch {
    return { ok: false, reason: 'error', email };
  }

  if (response.status === 429) {
    return { ok: false, reason: 'rate_limited', email };
  }
  if (response.status === 400) {
    return { ok: false, reason: 'rejected', email };
  }
  if (!response.ok) {
    return { ok: false, reason: 'error', email };
  }

  try {
    const body = (await response.json()) as {
      token?: string;
      user_uuid?: string;
      email?: string;
    };
    if (!body.token) return { ok: false, reason: 'error', email };
    return {
      ok: true,
      token: body.token,
      userUuid: body.user_uuid || '',
      email: body.email || email,
    };
  } catch {
    return { ok: false, reason: 'error', email };
  }
}
